import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import enquiriesStyles from '../enquiries/Enquiries.module.css';

// Document icon component
const DocumentIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '4rem', height: '4rem', color: 'var(--Purple-500)' }}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
  </svg>
);

const DocumentCard = ({ doc }) => {
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    
    // Handle Firestore Timestamp
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div 
      style={{
        backgroundColor: 'var(--Surface-Primary)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--spacing-l)',
        border: '1px solid var(--Stroke-Light)',
        boxShadow: 'var(--shadow-card)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        textAlign: 'center',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = 'var(--shadow-frame-big)';
        e.currentTarget.style.borderColor = 'var(--Purple-300)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'var(--shadow-card)';
        e.currentTarget.style.borderColor = 'var(--Stroke-Light)';
      }}
    >
      <div style={{ marginBottom: 'var(--spacing-m)' }}>
        <DocumentIcon />
      </div>
      <div style={{ flexGrow: 1, width: '100%' }}>
        <h3 style={{
          fontSize: 'var(--font-size-body2)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--Text-Primary)',
          marginBottom: 'var(--spacing-xs)',
        }}>
          {doc.name}
        </h3>
        <p style={{
          fontSize: 'var(--font-size-body4)',
          color: 'var(--Text-Secondary)',
          marginBottom: 'var(--spacing-s)',
        }}>
          {formatFileSize(doc.size)}
        </p>
        {doc.type && (
          <span style={{
            display: 'inline-block',
            padding: 'var(--spacing-xxs) var(--spacing-s)',
            fontSize: 'var(--font-size-body5)',
            backgroundColor: 'var(--Purple-100)',
            color: 'var(--Purple-700)',
            borderRadius: 'var(--radius-pill)',
            fontWeight: 'var(--font-weight-medium)',
          }}>
            {doc.type}
          </span>
        )}
      </div>
      <a
        href={doc.url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          width: '100%',
          marginTop: 'var(--spacing-l)',
          padding: 'var(--spacing-s) var(--spacing-m)',
          backgroundColor: 'var(--Surface-Primary)',
          color: 'var(--Text-Primary)',
          border: '1px solid var(--Stroke-Secondary)',
          borderRadius: 'var(--radius-xl)',
          fontSize: 'var(--font-size-body4)',
          fontWeight: 'var(--font-weight-medium)',
          textDecoration: 'none',
          display: 'inline-block',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--Surface-Hover)';
          e.currentTarget.style.borderColor = 'var(--Purple-400)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--Surface-Primary)';
          e.currentTarget.style.borderColor = 'var(--Stroke-Secondary)';
        }}
      >
        View & Download
      </a>
    </div>
  );
};

const DocumentsGrid = () => {
  const { jeweller } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'universal', 'specific'

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!jeweller?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');

        // Fetch jeweller-specific documents
        const specificDocsQuery = query(
          collection(db, 'documents'),
          where('jewellerId', '==', jeweller.id),
          orderBy('dateAdded', 'desc')
        );

        // Fetch universal documents
        const universalDocsQuery = query(
          collection(db, 'documents'),
          where('isUniversal', '==', true),
          orderBy('dateAdded', 'desc')
        );

        const [specificSnapshot, universalSnapshot] = await Promise.all([
          getDocs(specificDocsQuery),
          getDocs(universalDocsQuery)
        ]);

        const specificDocs = specificSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        const universalDocs = universalSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Combine and sort by date
        const allDocs = [...specificDocs, ...universalDocs].sort((a, b) => {
          const dateA = a.dateAdded?.toDate?.() || new Date(0);
          const dateB = b.dateAdded?.toDate?.() || new Date(0);
          return dateB - dateA;
        });

        setDocuments(allDocs);
      } catch (error) {
        console.error('Error fetching documents:', error);
        setError('Failed to load documents. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [jeweller]);

  const filteredDocuments = documents.filter(doc => {
    if (filter === 'all') return true;
    if (filter === 'universal') return doc.isUniversal === true;
    if (filter === 'specific') return doc.isUniversal !== true;
    return true;
  });

  const documentCounts = {
    all: documents.length,
    universal: documents.filter(d => d.isUniversal).length,
    specific: documents.filter(d => !d.isUniversal).length
  };

  if (loading) {
    return (
      <div className="animate-fadeIn">
        {/* Header - using Enquiries style */}
        <div className={enquiriesStyles.header}>
          <div className={enquiriesStyles.headerContent}>
            <div className={enquiriesStyles.headerText}>
              <h1 className={enquiriesStyles.headerTitle}>
                Your Documents
              </h1>
              <p className={enquiriesStyles.headerSubtitle}>
                Important files, contracts, guides, and brand assets
              </p>
            </div>
          </div>
        </div>

        <div style={{
          backgroundColor: 'var(--Surface-Primary)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--spacing-xxxl)',
          textAlign: 'center',
          boxShadow: 'var(--shadow-card)',
          border: '1px solid var(--Stroke-Light)',
        }}>
          <div style={{
            width: '3rem',
            height: '3rem',
            border: '3px solid var(--Grey-200)',
            borderTopColor: 'var(--Purple-600)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto var(--spacing-m)',
          }} />
          <p style={{ color: 'var(--Text-Secondary)', fontSize: 'var(--font-size-body3)' }}>
            Loading your documents...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="animate-fadeIn">
        {/* Header - using Enquiries style */}
        <div className={enquiriesStyles.header}>
          <div className={enquiriesStyles.headerContent}>
            <div className={enquiriesStyles.headerText}>
              <h1 className={enquiriesStyles.headerTitle}>
                Your Documents
              </h1>
              <p className={enquiriesStyles.headerSubtitle}>
                Important files, contracts, guides, and brand assets
              </p>
            </div>
          </div>
        </div>

        <div style={{
          backgroundColor: 'var(--Surface-Primary)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--spacing-xxxl)',
          textAlign: 'center',
          boxShadow: 'var(--shadow-card)',
          border: '1px solid var(--Stroke-Light)',
        }}>
          <p style={{ color: 'var(--Red-600)', marginBottom: 'var(--spacing-m)', fontSize: 'var(--font-size-body3)' }}>
            {error}
          </p>
          <button 
            onClick={() => window.location.reload()}
            style={{
              padding: 'var(--spacing-s) var(--spacing-l)',
              backgroundColor: 'var(--Button-Primary-Enabled-BG)',
              color: 'var(--Button-Primary-Text)',
              border: 'none',
              borderRadius: 'var(--button-radius)',
              fontSize: 'var(--font-size-button)',
              fontWeight: 'var(--button-font-weight)',
              cursor: 'pointer',
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      {/* Header - using Enquiries style */}
      <div className={enquiriesStyles.header}>
        <div className={enquiriesStyles.headerContent}>
          <div className={enquiriesStyles.headerText}>
            <h1 className={enquiriesStyles.headerTitle}>
              Your Documents
            </h1>
            <p className={enquiriesStyles.headerSubtitle}>
              Important files, contracts, guides, and brand assets
            </p>
          </div>
        </div>
      </div>

      {/* Filter Pills - styled like Enquiries */}
      {documents.length > 0 && (
        <div style={{ marginBottom: 'var(--spacing-l)' }}>
          <div
            style={{
              display: 'inline-flex',
              gap: 0,
              background: 'var(--Grey-50)',
              padding: '4px',
              borderRadius: 'var(--radius-full)',
            }}
          >
            <button
              onClick={() => setFilter('all')}
              style={{
                padding: 'var(--spacing-xs) var(--spacing-m)',
                background: filter === 'all' ? 'var(--Grey-900)' : 'transparent',
                border: 'none',
                borderRadius: 'var(--radius-full)',
                color: filter === 'all' ? 'var(--Text-Contrast)' : 'var(--Text-Secondary)',
                fontSize: 'var(--font-size-body4)',
                fontWeight: 'var(--font-weight-normal)',
                cursor: 'pointer',
                transition: 'var(--transition-all)',
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--spacing-xxs)',
                boxShadow: filter === 'all' ? 'var(--shadow-sm)' : 'none',
              }}
              onMouseEnter={(e) => {
                if (filter !== 'all') {
                  e.currentTarget.style.color = 'var(--Text-Primary)';
                } else {
                  e.currentTarget.style.background = 'var(--Grey-800)';
                }
              }}
              onMouseLeave={(e) => {
                if (filter !== 'all') {
                  e.currentTarget.style.color = 'var(--Text-Secondary)';
                } else {
                  e.currentTarget.style.background = 'var(--Grey-900)';
                }
              }}
            >
              All Documents ({documentCounts.all})
            </button>
            <button
              onClick={() => setFilter('specific')}
              style={{
                padding: 'var(--spacing-xs) var(--spacing-m)',
                background: filter === 'specific' ? 'var(--Grey-900)' : 'transparent',
                border: 'none',
                borderRadius: 'var(--radius-full)',
                color: filter === 'specific' ? 'var(--Text-Contrast)' : 'var(--Text-Secondary)',
                fontSize: 'var(--font-size-body4)',
                fontWeight: 'var(--font-weight-normal)',
                cursor: 'pointer',
                transition: 'var(--transition-all)',
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--spacing-xxs)',
                boxShadow: filter === 'specific' ? 'var(--shadow-sm)' : 'none',
              }}
              onMouseEnter={(e) => {
                if (filter !== 'specific') {
                  e.currentTarget.style.color = 'var(--Text-Primary)';
                } else {
                  e.currentTarget.style.background = 'var(--Grey-800)';
                }
              }}
              onMouseLeave={(e) => {
                if (filter !== 'specific') {
                  e.currentTarget.style.color = 'var(--Text-Secondary)';
                } else {
                  e.currentTarget.style.background = 'var(--Grey-900)';
                }
              }}
            >
              Your Documents ({documentCounts.specific})
            </button>
            <button
              onClick={() => setFilter('universal')}
              style={{
                padding: 'var(--spacing-xs) var(--spacing-m)',
                background: filter === 'universal' ? 'var(--Grey-900)' : 'transparent',
                border: 'none',
                borderRadius: 'var(--radius-full)',
                color: filter === 'universal' ? 'var(--Text-Contrast)' : 'var(--Text-Secondary)',
                fontSize: 'var(--font-size-body4)',
                fontWeight: 'var(--font-weight-normal)',
                cursor: 'pointer',
                transition: 'var(--transition-all)',
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--spacing-xxs)',
                boxShadow: filter === 'universal' ? 'var(--shadow-sm)' : 'none',
              }}
              onMouseEnter={(e) => {
                if (filter !== 'universal') {
                  e.currentTarget.style.color = 'var(--Text-Primary)';
                } else {
                  e.currentTarget.style.background = 'var(--Grey-800)';
                }
              }}
              onMouseLeave={(e) => {
                if (filter !== 'universal') {
                  e.currentTarget.style.color = 'var(--Text-Secondary)';
                } else {
                  e.currentTarget.style.background = 'var(--Grey-900)';
                }
              }}
            >
              General Resources ({documentCounts.universal})
            </button>
          </div>
        </div>
      )}

      {/* Grid of Documents */}
      {filteredDocuments.length > 0 ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 'var(--spacing-l)',
          marginBottom: 'var(--spacing-xxl)',
        }}>
          {filteredDocuments.map((doc) => (
            <DocumentCard key={doc.id} doc={doc} />
          ))}
        </div>
      ) : documents.length > 0 ? (
        <div style={{
          backgroundColor: 'var(--Surface-Primary)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--spacing-xxxl)',
          textAlign: 'center',
          boxShadow: 'var(--shadow-card)',
          border: '1px solid var(--Stroke-Light)',
        }}>
          <h3 style={{
            fontSize: 'var(--font-size-h5)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--Text-Primary)',
            marginBottom: 'var(--spacing-xs)',
          }}>
            No Documents in This Category
          </h3>
          <p style={{ color: 'var(--Text-Secondary)', fontSize: 'var(--font-size-body3)' }}>
            Try selecting a different filter to view other documents.
          </p>
        </div>
      ) : (
        <div style={{
          backgroundColor: 'var(--Surface-Primary)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--spacing-xxxl)',
          textAlign: 'center',
          boxShadow: 'var(--shadow-card)',
          border: '1px solid var(--Stroke-Light)',
        }}>
          <DocumentIcon />
          <h3 style={{
            fontSize: 'var(--font-size-h5)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--Text-Primary)',
            marginTop: 'var(--spacing-m)',
            marginBottom: 'var(--spacing-xs)',
          }}>
            No Documents Found
          </h3>
          <p style={{ color: 'var(--Text-Secondary)', fontSize: 'var(--font-size-body3)' }}>
            When we share a document with you, it will appear here.
          </p>
        </div>
      )}
    </div>
  );
};

export default DocumentsGrid;